import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { LeadKanbanBoard, KanbanSortMode, LeadKanbanCard, LeadQuery } from '@troia-v3/types';
import { useCallback, useState } from 'react';

import { leadsService } from '../services';

interface UseLeadsKanbanParams {
  funnelId: string | null;
  search?: string;
  filters?: LeadQuery['filters'];
  windowSize?: number;
  sortMode?: KanbanSortMode;
}

/**
 * Board kanban com janelas por coluna. Counts/totalValue/busca vêm do backend;
 * `loadMore` anexa páginas por cursor; `applyCardMove` faz cirurgia de cache
 * pós-move (sem refetch do board — preserva o scroll das colunas).
 */
export function useLeadsKanban({
  funnelId,
  search,
  filters,
  windowSize = 50,
  sortMode = 'created',
}: UseLeadsKanbanParams) {
  const queryClient = useQueryClient();
  const queryKey = ['leads-kanban', funnelId, search ?? '', filters ?? {}, sortMode] as const;

  const query = useQuery({
    queryKey,
    queryFn: () =>
      leadsService.getKanbanBoard({
        funnelId: funnelId as string,
        ...(search ? { search } : {}),
        ...(filters ? { filters } : {}),
        windowSize,
        sortMode,
      }),
    enabled: !!funnelId,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  const [loadingColumns, setLoadingColumns] = useState<Record<string, boolean>>({});
  const [errorColumns, setErrorColumns] = useState<Record<string, boolean>>({});

  const loadMore = useCallback(
    async (columnId: string) => {
      const data = queryClient.getQueryData<LeadKanbanBoard>(queryKey);
      const column = data?.columns.find((c) => c.columnId === columnId);
      if (!column?.nextCursor || loadingColumns[columnId] || !funnelId) return;

      setLoadingColumns((prev) => ({ ...prev, [columnId]: true }));
      setErrorColumns((prev) => ({ ...prev, [columnId]: false }));
      try {
        const page = await leadsService.getKanbanColumn({
          funnelId,
          stepId: columnId,
          cursor: column.nextCursor,
          ...(search ? { search } : {}),
          ...(filters ? { filters } : {}),
          windowSize,
          sortMode,
        });
        queryClient.setQueryData<LeadKanbanBoard>(queryKey, (old) =>
          old
            ? {
                ...old,
                columns: old.columns.map((c) => {
                  if (c.columnId !== columnId) return c;
                  // Dedupe por id: um card movido via applyCardMove (inserido no topo
                  // da coluna destino) já persiste no DB com o novo stepId antes desse
                  // fetch resolver — se o createdAt dele cair na janela da próxima
                  // página por cursor, o backend o retorna de novo e duplicaria o item.
                  const seen = new Set(c.items.map((i) => i.id));
                  return {
                    ...c,
                    items: [...c.items, ...page.items.filter((i) => !seen.has(i.id))],
                    nextCursor: page.nextCursor,
                    count: page.count,
                    totalValue: page.totalValue,
                  };
                }),
              }
            : old,
        );
      } catch (error) {
        const status = (error as { response?: { status?: number } }).response?.status;
        if (status === 422) {
          // Cursor inválido/legado (formato SP1 ou de outro sortMode): re-tentar o
          // mesmo cursor é beco sem saída — limpa e refaz o board (spec SP2 §6.1).
          queryClient.setQueryData<LeadKanbanBoard>(queryKey, (old) =>
            old
              ? { ...old, columns: old.columns.map((c) => (c.columnId === columnId ? { ...c, nextCursor: null } : c)) }
              : old,
          );
          void queryClient.invalidateQueries({ queryKey: ['leads-kanban'] });
        } else {
          setErrorColumns((prev) => ({ ...prev, [columnId]: true }));
        }
      } finally {
        setLoadingColumns((prev) => ({ ...prev, [columnId]: false }));
      }
    },
    [queryClient, queryKey, funnelId, search, filters, windowSize, sortMode, loadingColumns],
  );

  const applyCardMove = useCallback(
    (card: LeadKanbanCard, fromColumnId: string, toColumnId: string) => {
      queryClient.setQueryData<LeadKanbanBoard>(queryKey, (old) => {
        if (!old) return old;
        const movedCard: LeadKanbanCard = { ...card, stepId: toColumnId };
        return {
          ...old,
          columns: old.columns.map((c) => {
            if (c.columnId === fromColumnId) {
              return {
                ...c,
                items: c.items.filter((i) => i.id !== card.id),
                count: Math.max(0, c.count - 1),
                totalValue: c.totalValue - (card.budget ?? 0),
              };
            }
            if (c.columnId === toColumnId) {
              return {
                ...c,
                items: [movedCard, ...c.items],
                count: c.count + 1,
                totalValue: c.totalValue + (card.budget ?? 0),
              };
            }
            return c;
          }),
        };
      });
    },
    [queryClient, queryKey],
  );

  const applyCardReorder = useCallback(
    (card: LeadKanbanCard, fromColumnId: string, toColumnId: string, insertBeforeId: string | null) => {
      queryClient.setQueryData<LeadKanbanBoard>(queryKey, (old) => {
        if (!old) return old;
        const isCross = fromColumnId !== toColumnId;
        return {
          ...old,
          columns: old.columns.map((c) => {
            let items = c.items;
            let count = c.count;
            let totalValue = c.totalValue;
            if (c.columnId === fromColumnId) {
              items = items.filter((i) => i.id !== card.id);
              if (isCross) {
                count = Math.max(0, count - 1);
                totalValue -= card.budget ?? 0;
              }
            }
            if (c.columnId === toColumnId) {
              const base = items.filter((i) => i.id !== card.id);
              const idx = insertBeforeId ? base.findIndex((i) => i.id === insertBeforeId) : -1;
              const at = idx === -1 ? base.length : idx;
              items = [...base.slice(0, at), card, ...base.slice(at)];
              if (isCross) {
                count += 1;
                totalValue += card.budget ?? 0;
              }
            }
            return items === c.items ? c : { ...c, items, count, totalValue };
          }),
        };
      });
    },
    [queryClient, queryKey],
  );

  return {
    boardData: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    loadMore,
    applyCardMove,
    applyCardReorder,
    loadingColumns,
    errorColumns,
  };
}
